
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminLayout from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/SupabaseAuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { CreditCard, Wallet, ShoppingCart, FileText, Plus, Search, Download, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import AdminWallet from "@/components/masterlicense/wallet/AdminWallet";
import RewardInvoices from "@/components/masterlicense/wallet/RewardInvoices";
import WalletRecentCharges from "@/components/masterlicense/wallet/WalletRecentCharges";

const AdminRewardPurchasePage: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("wallet");
  const [searchQuery, setSearchQuery] = useState("");
  const [autoRefill, setAutoRefill] = useState(false);
  const [minimumBalance, setMinimumBalance] = useState("500");
  const [refillAmount, setRefillAmount] = useState("1000");

  // Available gift cards (mock data)
  const giftCards = [
    { id: 'gc1', name: 'Amazon', logo: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png', denominations: [25, 50, 100] },
    { id: 'gc2', name: 'Apple Store', logo: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png', denominations: [15, 25, 50, 100] },
    { id: 'gc3', name: 'Best Buy', logo: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png', denominations: [25, 50, 100, 200] },
    { id: 'gc4', name: 'Target', logo: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png', denominations: [10, 25, 50, 100] },
    { id: 'gc5', name: 'Starbucks', logo: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png', denominations: [5, 10, 25, 50] },
    { id: 'gc6', name: 'Walmart', logo: '/lovable-uploads/e2e58de0-5228-4c21-8754-a52246e544e0.png', denominations: [25, 50, 100, 250] }
  ];

  // Purchased cards (mock data)
  const purchasedCards = [
    { id: 'p1', cardName: 'Amazon', amount: 50, recipientEmail: 'user@example.com', date: '2025-04-15', status: 'delivered' },
    { id: 'p2', cardName: 'Target', amount: 25, recipientEmail: 'john@example.com', date: '2025-04-12', status: 'delivered' },
    { id: 'p3', cardName: 'Best Buy', amount: 100, recipientEmail: 'sarah@example.com', date: '2025-04-10', status: 'failed' },
    { id: 'p4', cardName: 'Apple Store', amount: 50, recipientEmail: 'mike@example.com', date: '2025-04-08', status: 'pending' }
  ];

  const filteredCards = giftCards.filter(card => 
    card.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAutoRefillChange = (checked: boolean) => {
    setAutoRefill(checked);
    if (checked) {
      toast({
        title: "Auto-refill enabled",
        description: `Your wallet will be automatically refilled with $${refillAmount} when balance falls below $${minimumBalance}.`
      });
    } else {
      toast({
        title: "Auto-refill disabled",
        description: "Your wallet will no longer be automatically refilled."
      });
    }
  };

  const handleSaveWalletSettings = () => {
    toast({
      title: "Wallet settings saved",
      description: "Your wallet settings have been updated successfully."
    });
  };

  const purchaseCard = (cardName: string, amount: number) => {
    toast({
      title: "Purchase initiated",
      description: `Processing purchase of $${amount} ${cardName} gift card.`,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <AdminLayout onLogout={() => {}} userRole={user?.role}>
      <div className="container mx-auto py-6 space-y-6">
        <h1 className="text-2xl font-bold mb-6">Reward Purchases & Wallet</h1>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="wallet" className="flex items-center">
              <Wallet className="mr-2 h-4 w-4" />
              Wallet
            </TabsTrigger>
            <TabsTrigger value="gift-cards" className="flex items-center">
              <ShoppingCart className="mr-2 h-4 w-4" />
              Gift Cards
            </TabsTrigger>
            <TabsTrigger value="purchases" className="flex items-center">
              <FileText className="mr-2 h-4 w-4" />
              Purchase Reports
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center">
              <CreditCard className="mr-2 h-4 w-4" />
              Wallet Settings
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="wallet">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <AdminWallet adminId={user?.id || ""} />
              <WalletRecentCharges adminId={user?.id || ""} />
            </div>
            <div className="mt-6">
              <RewardInvoices adminId={user?.id || ""} />
            </div>
          </TabsContent>
          
          <TabsContent value="gift-cards">
            <Card>
              <CardHeader>
                <CardTitle>Gift Card Catalog</CardTitle>
                <CardDescription>
                  Browse and purchase gift cards for your referral rewards program
                </CardDescription>
                <div className="flex w-full max-w-sm items-center space-x-2 mt-4">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Search gift cards..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCards.map((card) => (
                    <Card key={card.id} className="overflow-hidden">
                      <div className="h-32 bg-muted flex items-center justify-center">
                        <img 
                          src={card.logo} 
                          alt={card.name} 
                          className="h-16 w-auto object-contain"
                        />
                      </div>
                      <CardHeader className="pb-2">
                        <CardTitle>{card.name} Gift Card</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {card.denominations.map(amount => (
                            <Badge key={`${card.id}-${amount}`} variant="outline" className="cursor-pointer hover:bg-primary hover:text-primary-foreground">
                              ${amount}
                            </Badge>
                          ))}
                        </div>
                        <Button 
                          className="w-full" 
                          onClick={() => purchaseCard(card.name, card.denominations[0])}
                        >
                          Purchase
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="purchases">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Purchase History</CardTitle>
                  <CardDescription>
                    View all your gift card purchases and their status
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Button variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Gift Card</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Purchase Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchasedCards.map(card => (
                      <TableRow key={card.id}>
                        <TableCell className="font-medium">{card.cardName}</TableCell>
                        <TableCell>${card.amount}</TableCell>
                        <TableCell>{card.recipientEmail}</TableCell>
                        <TableCell>{formatDate(card.date)}</TableCell>
                        <TableCell>
                          <Badge variant={
                            card.status === 'delivered' ? 'default' : 
                            card.status === 'pending' ? 'outline' : 'destructive'
                          }>
                            {card.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Wallet Settings</CardTitle>
                <CardDescription>
                  Configure your wallet auto-refill settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-refill">Auto-refill</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically add funds when your balance is low
                    </p>
                  </div>
                  <Switch
                    id="auto-refill"
                    checked={autoRefill}
                    onCheckedChange={handleAutoRefillChange}
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="minimum-balance">Minimum Balance Threshold ($)</Label>
                    <Input
                      id="minimum-balance"
                      type="number"
                      value={minimumBalance}
                      onChange={(e) => setMinimumBalance(e.target.value)}
                      className="mt-1"
                      disabled={!autoRefill}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-refill will trigger when your balance falls below this amount
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="refill-amount">Refill Amount ($)</Label>
                    <Input
                      id="refill-amount"
                      type="number"
                      value={refillAmount}
                      onChange={(e) => setRefillAmount(e.target.value)}
                      className="mt-1"
                      disabled={!autoRefill}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This amount will be added to your wallet when auto-refill is triggered
                    </p>
                  </div>
                </div>
                
                <div className="pt-4">
                  <Button 
                    onClick={handleSaveWalletSettings}
                    disabled={!autoRefill}
                  >
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Payment Methods</CardTitle>
                <CardDescription>
                  Manage payment methods for wallet funding
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between border p-4 rounded-md">
                    <div className="flex items-center gap-4">
                      <CreditCard className="h-8 w-8 text-primary" />
                      <div>
                        <p className="font-medium">Credit Card ending in 4242</p>
                        <p className="text-sm text-muted-foreground">Expires 12/25</p>
                      </div>
                    </div>
                    <Badge>Default</Badge>
                  </div>
                  
                  <Button className="flex gap-2">
                    <Plus className="h-4 w-4" />
                    Add Payment Method
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminRewardPurchasePage;
